import React, { useContext, useState, useEffect, useCallback  } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import QuoteFormItemsTable from "./QuoteFormItemsTable";
import { Quote, QuoteItem, getQuoteWithItemsById, postQuoteWithItems } from "../../../api/quoteApi";
import DateInput from "../../../Components/shared/DateInput";
import { AuthContext } from "../../../Components/auth/authContext";
import { useUser } from "../../../Components/context/UserContext";
import { getTodayDate, getFutureDate } from "../../../utils/dateHelpers";
import { AccountInfo, getAccountByUserId } from "../../../api/companyInfoApi";
import { isValidQuoteExpirationDate } from "../../../utils/validationHelpers";
import { Customer, getCustomerById } from "../../../api/customersAPI";
import { CustomerAddress, getCustomerAddressesByCustomerId } from "../../../api/customerAddressAPI";
import AlertMessage from "../../../Components/Layout/Messages/AlertMessage";
import AddressInformation from "./AddressInformation";
import { useLoading } from "../../../Components/context/LoadingContext";
import QuoteInformation from "./QuoteInformation";
import QuoteFormActionMenu from "./QuoteFormActionMenu";

const QuoteForm = () => {
  const currentUser = useContext(AuthContext);
  const { userIdentity } = useUser();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState<string>('');
  const { customerId, quoteID } = useParams<{
    customerId?: string;
    quoteID?: string;
  }>();

  // State
  const [quote, setQuote] = useState<Quote>({
    quoteId: 0,
    accountId: userIdentity?.accountId || 0,
    userId: userIdentity?.userId || 0,
    customerId: 0,
    quoteDate: getTodayDate(),
    expirationDate: getFutureDate(30),
    dueDate: "2099-12-31",
    totalAmount: 0,
    statusID: 0,
    statusName: "draft",
    dealName: "",
  });
  const [items, setItems] = useState<QuoteItem[]>([
    {
      quoteItemID: 0,
      quoteID: 0,
      productID: 0,
      itemName: "",
      itemDescription: "",
      quantity: 1,
      price: 0,
    },
  ]);
  const [companyInfo, setCompanyInfo] = useState<AccountInfo | null>(null);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [customerAddresses, setCustomerAddresses] =
    useState<CustomerAddress[]>([]);
  const { setLoading } = useLoading();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [derivedCustomerId, setDerivedCustomerId] = useState<number | null>(
    customerId ? Number(customerId) : null
  );

  // Data Fetching
  const fetchQuoteDetails = useCallback(async () => {
    if (quoteID) {
      try {
        setLoading(true);
        const data = await getQuoteWithItemsById(Number(quoteID));
        setQuote(data.quote);
        setItems(data.quoteItems);
        if (!customerId) {
          setDerivedCustomerId(data.quote.customerId);
        }
      } catch (err) {
        setError("Failed to fetch quote details.");
      } finally {
        setLoading(false);
      }
    }
  }, [quoteID, customerId]);

  useEffect(() => {
    const fetchData = async () => {
      if (userIdentity?.userId) {
        try {
          setLoading(true);
          const promises = [
            getAccountByUserId(userIdentity.userId),
            derivedCustomerId
              ? getCustomerById(derivedCustomerId)
              : Promise.resolve(null),
            derivedCustomerId
              ? getCustomerAddressesByCustomerId(derivedCustomerId)
              : Promise.resolve([]),
          ];
          const [companyData, customerData, customerAddresses] =
            await Promise.all(promises);

          setCompanyInfo(companyData);
          if (customerData) setCustomerData(customerData);
          setCustomerAddresses(customerAddresses || []);
        } catch (err) {
          setError("Failed to fetch data.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
    fetchQuoteDetails();
  }, [userIdentity, derivedCustomerId, fetchQuoteDetails]);

  // Event Handlers
  const handleAddItem = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setItems([
      ...items,
      {
        quoteItemID: 0,
        quoteID: quote.quoteId,
        productID: 0,
        itemName: "",
        itemDescription: "",
        quantity: 1,
        price: 0,
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleInputChange = (
    index: number,
    field: keyof typeof items[0],
    value: string | number
  ) => {
    setItems((prevItems) =>
      prevItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const totalAmount = items.reduce(
        (acc, item) => acc + item.quantity * item.price,
        0,
      );

      const payload = {
        quote: {
          ...quote,
          totalAmount,
          customerId: Number(derivedCustomerId || customerId || 0),
        },
        quoteItems: items,
      };

      await postQuoteWithItems(payload);
      setSuccess("Quote Saved successfully.");

    } catch (error) {
      // Handle errors here
      console.error("Error submitting quote:", error);
      setError("Failed to save quote.");
    }
  };

  const validateForm = (): boolean => {
    const expirationValidation = isValidQuoteExpirationDate(
      quote.expirationDate,
    );
    const newErrors: Record<string, string | null> = {};

    if (!expirationValidation.valid) {
      newErrors.expirationDate = expirationValidation.message;
    }

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => !error);
  };

  return (
    <div id="kt_app_content_container" className="app-container container-xxl">
      <div className="d-flex flex-column flex-lg-row">
        <div className="flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7 me-xl-10">
          <div className="card">
            <div className="card-body p-12">
              <form action="" id="kt_invoice_form">
                <AlertMessage error={error} success={success} successTimeout={4} />
                <div className="d-flex flex-column align-items-start flex-xxl-row">
                  <div
                    className="d-flex align-items-center flex-equal fw-row me-4 order-2"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    title="Specify quote date"
                  >
                    <div className="fs-6 fw-bold text-gray-700 text-nowrap">
                      Quote Date:
                    </div>
                    <div className="position-relative d-flex align-items-center w-150px">
                    <DateInput
                        label="Quote Date"
                        name="quoteDate"
                        value={quote.quoteDate}
                        onChange={(date) => setQuote((prevQuote) => ({ ...prevQuote, quoteDate: date }))}
                      />

                      
                    </div>
                  </div>
                  <div
                    className="d-flex flex-center flex-equal fw-row text-nowrap order-1 order-xxl-2 me-4"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    title="Enter quote number"
                  >
                    <span className="fs-2x fw-bold text-gray-800">
                      Quote #
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-flush fw-bold text-muted fs-3 w-125px"
                      value={quote.quoteId || ""}
                      placeholder="..."
                      readOnly // Prevent editing since it is an auto-generated field
                    />

                  </div>
                  
                  <div
                    className="d-flex align-items-center justify-content-end flex-equal order-3 fw-row"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    title="Specify invoice due date"
                  >
                    <div className="fs-6 fw-bold text-gray-700 text-nowrap">
                      Expiration Date:
                    </div>
                    <div className="position-relative d-flex align-items-center w-150px">
                    <DateInput
                        label="Expiration Date"
                        name="expirationDate"
                        value={quote.expirationDate}
                        onChange={(date) => setQuote((prevQuote) => ({ ...prevQuote, expirationDate: date }))}
                      />
                    </div>
                    <br/>{errors.expirationDate && (<div className="text-danger mt-1">{errors.expirationDate}</div>)}
                  </div>

                </div>
                <div className="separator separator-dashed my-10"></div>
                <QuoteInformation quote={quote} setQuote={setQuote} />
                <div className="separator separator-dashed my-10"></div>

                <div className="mb-0">

                <AddressInformation
                    companyInfo={companyInfo}
                    customerData={customerData}
                    customerAddresses={customerAddresses}
                  />

                  <QuoteFormItemsTable
                        items={items}
                        onAdd={handleAddItem}
                        onRemove={handleRemoveItem}
                        onChange={handleInputChange}
                    />

                  <div className="mb-5">
                    <label className="form-label fs-6 fw-bold text-gray-700">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      className="form-control form-control-solid"
                      rows={3}
                      placeholder="Thanks for your business"
                      defaultValue={""}
                    />
                  </div>


                  <div className="mb-0">
                    <label className="form-label fs-6 fw-bold text-gray-700">
                      Terms and Conditions
                    </label>
                    <textarea
                      name="notes"
                      className="form-control form-control-solid"
                      rows={3}
                      placeholder="Enter your terms and conditions, if any, or set a global value for the account."
                      defaultValue={""}
                    />
                  </div>  


                </div>
              </form>
            </div>
          </div>
        </div>

        <QuoteFormActionMenu currency={currency} setCurrency={setCurrency} handleSubmit={handleSubmit} quoteID={Number(quoteID)} />

      </div>
    </div>

);
};

export default QuoteForm;
